import { useEffect, useRef, useState } from 'react';
import './CompareSection.css';

const FEATURES = [
  'WhatsApp Business API (Official)',
  'AI Email Assistant (Gmail)',
  'AI Chatbot (ChatGPT/Gemini/Claude)',
  'No-Code Flow Builder',
  'Unlimited Contacts',
  'Unlimited Automations',
  'Multi-Agent Inbox',
  'Broadcast Campaigns',
  'E-Commerce Integration (Shopify/WooCommerce)',
  'Implementation Support',
  'Competitive Pricing',
  'Dedicated Account Manager',
  '24/7 Customer Support',
  'Real-Time Analytics Dashboard',
];

const COMPETITORS = [
  { name: 'Zepliq',    highlight: true,  values: [true, true, true, true, true, true, true, true, true, true, true, true, true, true] },
  { name: 'WATI',      highlight: false, values: [true, false, true, true, false, false, true, true, true, false, false, false, false, true] },
  { name: 'ManyChat',  highlight: false, values: [false, false, true, true, false, false, true, true, false, false, false, false, false, true] },
  { name: 'WhatBot',   highlight: false, values: [true, false, false, true, true, true, true, true, false, true, true, false, false, false] },
];

const Check = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="compare__check">
    <circle cx="10" cy="10" r="10" fill="rgba(99,102,241,0.14)" />
    <path d="M6 10l3 3 5-5" stroke="#6366F1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const Cross = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="compare__cross">
    <circle cx="10" cy="10" r="10" fill="rgba(239,68,68,0.08)" />
    <path d="M7 7l6 6M13 7l-6 6" stroke="#EF4444" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

export default function CompareSection() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section className="compare section" ref={ref}>
      <div className="container">
        <div className={`compare__header ${visible ? 'compare__header--visible' : ''}`}>
          <span className="badge badge-brand">Why Zepliq</span>
          <h2 className="display-lg compare__title">
            The clear <span className="gradient-text">winner</span><br />in every category
          </h2>
          <p className="text-lg compare__sub">
            See how Zepliq stacks up against the competition. We're not just better, we're in a different league.
          </p>
        </div>

        <p className="compare__scroll-hint" aria-hidden="true">← Swipe to compare →</p>
        <div className={`compare__table-wrap ${visible ? 'compare__table-wrap--visible' : ''}`}>
          <div className="compare__table" role="table" aria-label="Feature comparison">

            {/* Column headers */}
            <div className="compare__row compare__row--header" role="row">
              <div className="compare__cell compare__cell--feature" role="columnheader">Features</div>
              {COMPETITORS.map(c => (
                <div
                  key={c.name}
                  className={`compare__cell compare__cell--brand ${c.highlight ? 'compare__cell--highlight' : ''}`}
                  role="columnheader"
                >
                  {c.highlight && <div className="compare__best-badge">⭐ Best</div>}
                  <span className="compare__brand-name">{c.name}</span>
                </div>
              ))}
            </div>

            {/* Feature rows */}
            {FEATURES.map((feature, fi) => (
              <div
                key={feature}
                className={`compare__row compare__row--data ${fi % 2 === 0 ? 'compare__row--even' : ''}`}
                role="row"
                style={{ animationDelay: `${fi * 0.04}s` }}
              >
                <div className="compare__cell compare__cell--feature" role="cell">
                  <span className="compare__feature-name">{feature}</span>
                </div>
                {COMPETITORS.map(c => (
                  <div
                    key={c.name}
                    className={`compare__cell compare__cell--value ${c.highlight ? 'compare__cell--highlight' : ''}`}
                    role="cell"
                    aria-label={`${c.name}: ${c.values[fi] ? 'Yes' : 'No'}`}
                  >
                    {c.values[fi] ? <Check /> : <Cross />}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>

        <div className="compare__footer">
          <p className="text-sm compare__note">
            * Based on publicly available information as of 2025. Features subject to change.
          </p>
          <a href="/contact" className="btn btn-primary btn-lg">
            Start with Zepliq, It's Free →
          </a>
        </div>
      </div>
    </section>
  );
}
